import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState, useEffect } from "react";
import { Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, Box, Flex, } from "@chakra-ui/react";
const SwipeDownDrawer = ({ isOpen, onOpen, onClose, content, header, footer, showCloseButton = false, defaultHeight, bg = "#1E1E1E", color = "white", mode, topText, }) => {
    const drawerContentRef = useRef(null);
    const drawerBodyRef = useRef(null);
    const contentWrapperRef = useRef(null);
    const [translateY, setTranslateY] = useState(0);
    const touchStartYRef = useRef(0);
    const touchStartTimeRef = useRef(0);
    const [isDragging, setIsDragging] = useState(false);
    const [contentHeight, setContentHeight] = useState(undefined);
    const isPaxMode = mode === "pax";
    const checkIsAtTop = useCallback(() => {
        if (drawerBodyRef.current) {
            return drawerBodyRef.current.scrollTop === 0;
        }
        return true;
    }, []);
    const handleTouchStart = (e) => {
        // Only allow swipe if we're at the top of the scrollable content
        if (!checkIsAtTop()) {
            return;
        }
        const startY = e.touches[0].clientY;
        touchStartYRef.current = startY;
        touchStartTimeRef.current = Date.now();
        setIsDragging(true);
    };
    const handleTouchMove = (e) => {
        if (!drawerContentRef.current || !isDragging)
            return;
        // Double-check we're still at the top
        if (!checkIsAtTop()) {
            setIsDragging(false);
            setTranslateY(0);
            return;
        }
        const currentY = e.touches[0].clientY;
        const deltaY = currentY - touchStartYRef.current;
        // Only allow downward swipes
        if (deltaY > 0) {
            setTranslateY(deltaY);
        }
    };
    const handleTouchEnd = () => {
        if (!isDragging) {
            return;
        }
        const swipeDistance = translateY;
        const swipeDuration = Date.now() - touchStartTimeRef.current;
        const swipeVelocity = swipeDistance / swipeDuration; // pixels per ms
        // Require either:
        // 1. A significant swipe distance (150px or more), OR
        // 2. A fast swipe (velocity > 0.5 px/ms) with at least 80px distance
        const shouldClose = swipeDistance > 150 || (swipeVelocity > 0.5 && swipeDistance > 80);
        if (shouldClose && checkIsAtTop()) {
            onClose();
        }
        setTranslateY(0);
        setIsDragging(false);
    };
    // Track content height changes for smooth transitions
    useEffect(() => {
        if (!isOpen) {
            // Reset height when drawer closes
            setContentHeight(undefined);
            return;
        }
        if (!contentWrapperRef.current)
            return;
        const updateHeight = () => {
            if (contentWrapperRef.current) {
                // Use double requestAnimationFrame to ensure DOM has updated
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (contentWrapperRef.current) {
                            // scrollHeight gives us the full content height even if constrained
                            const height = contentWrapperRef.current.scrollHeight;
                            if (height > 0) {
                                setContentHeight(height);
                            }
                        }
                    });
                });
            }
        };
        // Initial height measurement with a small delay to ensure drawer is rendered
        const timeoutId = setTimeout(() => {
            updateHeight();
        }, 50);
        // Use ResizeObserver to watch for content size changes
        // Observe the wrapper which contains all content
        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });
        resizeObserver.observe(contentWrapperRef.current);
        // Also observe the body content which is what usually changes
        const bodyObserver = drawerBodyRef.current
            ? new ResizeObserver(() => {
                updateHeight();
            })
            : null;
        if (drawerBodyRef.current && bodyObserver) {
            bodyObserver.observe(drawerBodyRef.current);
        }
        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            if (bodyObserver) {
                bodyObserver.disconnect();
            }
        };
    }, [isOpen, content, header, footer]);
    // Calculate max height (90vh)
    const maxHeight = typeof window !== "undefined" ? window.innerHeight * 0.9 : undefined;
    const finalHeight = defaultHeight
        ? defaultHeight
        : contentHeight && maxHeight
            ? `${Math.min(contentHeight, maxHeight)}px`
            : undefined;
    return (_jsxs(Drawer, { isOpen: isOpen, placement: "bottom", onClose: onClose, finalFocusRef: drawerContentRef, children: [_jsx(DrawerOverlay, {}), _jsxs(DrawerContent, { ref: drawerContentRef, bg: bg, color: color, borderTopRadius: 24, pt: 4, maxH: isPaxMode ? "98vh" : "90vh", h: finalHeight ? `${finalHeight}` : "auto", transform: `translateY(${translateY}px)`, transition: "transform 0.1s ease, height 0.3s ease-in-out", onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: handleTouchEnd, zIndex: 99999, children: [_jsx(Flex, { w: "100%", alignItems: "center", justifyContent: "center", position: "absolute", top: 4, zIndex: 99999, children: _jsx(Box, { w: "50px", h: "3px", bg: "#7e7e7e", borderRadius: 10 }) }), showCloseButton && _jsx(DrawerCloseButton, { zIndex: 99999 }), _jsxs(Box, { ref: contentWrapperRef, w: "100%", children: [header && _jsx(DrawerHeader, { textAlign: "center", children: header }), _jsxs(DrawerBody, { ref: drawerBodyRef, overflow: "auto", flex: "1", maxH: isPaxMode
                                    ? "calc(100vh - 120px)"
                                    : finalHeight
                                        ? `${finalHeight}`
                                        : "auto", p: 0, children: [(topText === null || topText === void 0 ? void 0 : topText.trim()) && (_jsx(Flex, { position: "absolute", top: "-35px", right: "16px", align: "center", px: "12px", py: "8px", bg: "rgba(90,60,180,0.9)", backdropFilter: "blur(8px)", borderRadius: "full", cursor: "pointer", zIndex: 999999, boxShadow: "0 0 12px rgba(168,85,247,0.6)", _hover: {
                                            boxShadow: "0 0 18px rgba(168,85,247,0.85)",
                                        }, transition: "all 0.18s ease", onClick: onClose, children: _jsx(Box, { fontSize: "10px", fontWeight: "500", letterSpacing: "0.6px", textTransform: "uppercase", color: "white", userSelect: "none", lineHeight: "1", children: topText }) })), content] }), footer ? _jsx(DrawerFooter, { children: footer }) : null] })] })] }));
};
export default SwipeDownDrawer;
